<script lang="ts">
  import { onMount } from 'svelte';
  import {
    AURAS,
    CLASSES,
    CLASS_IDS,
    abilityDef,
    spellbook,
    auraDef,
    type AbilityId,
    type AuraId,
    type ClassId,
  } from '@arena/shared';
  import { CLASS_LOOKS, HELD_ITEMS, type HeldItem } from '../game/UnitView';
  import {
    CHARACTERS,
    ViewerEngine,
    type Stance,
    type Team,
  } from '../viewer/ViewerEngine';
  import { TRAIL_STYLES, type TrailStyle } from '../game/WeaponTrail';

  const ITEMS = [null, ...Object.keys(HELD_ITEMS)] as (HeldItem | null)[];
  const SIDES = ['right', 'left'] as const;
  const TEAMS: Team[] = ['self', 'friendly', 'enemy'];
  const STANCES: Stance[] = [
    'idle',
    'walk',
    'backpedal',
    'run',
    'jump',
    'fall',
    'casting',
    'dead',
  ];
  const AURA_IDS = Object.keys(AURAS) as AuraId[];
  const TRAIL_STYLE_IDS = Object.keys(TRAIL_STYLES) as TrailStyle[];

  let canvas: HTMLCanvasElement;
  let overlay: HTMLDivElement;
  let engine: ViewerEngine | undefined = $state();
  let clips = $state<string[]>([]);
  let classId = $state<ClassId>('warrior');
  let character = $state(CLASS_LOOKS.warrior.character);
  let hands = $state({ ...CLASS_LOOKS.warrior.hands });
  let team = $state<Team>('enemy');
  let stance = $state<Stance>('idle');
  let auras = $state<AuraId[]>([]);
  let health = $state(1);
  let targeted = $state(false);
  let timeScale = $state(1);
  let autoRotate = $state(false);
  let dummy = $state(true);
  let distance = $state(8);
  let circling = $state(false);
  let trailClass = $state<ClassId | null>(null);
  let trailStyle = $state<TrailStyle>('ribbon');
  let trailSeconds = $state(0.35);

  onMount(() => {
    const viewer = new ViewerEngine(canvas, overlay);
    engine = viewer;
    void viewer.start().then((names) => (clips = names));
    return () => viewer.dispose();
  });

  $effect(() =>
    engine?.setLook({ classId, character, hands: { ...hands }, team }),
  );
  $effect(() => engine?.setAutoRotate(autoRotate));
  $effect(() => engine?.setDummy(dummy));
  $effect(() =>
    engine?.setTrail(
      trailClass && {
        classId: trailClass,
        style: trailStyle,
        seconds: trailSeconds,
      },
    ),
  );
  $effect(() => {
    if (!engine) return;
    engine.stance = stance;
    engine.auras = [...auras];
    engine.health = health;
    engine.targeted = targeted;
    engine.timeScale = timeScale;
    engine.distance = distance;
    engine.circling = circling;
  });

  function pickClass(id: ClassId) {
    classId = id;
    character = CLASS_LOOKS[id].character;
    hands = { ...CLASS_LOOKS[id].hands };
  }

  function castLabel(id: AbilityId): string {
    const ability = abilityDef(id);
    if (ability.channel)
      return `${ability.name} (${ability.channel.duration}s channel)`;
    if (ability.castTime) return `${ability.name} (${ability.castTime}s)`;
    return ability.name;
  }
</script>

<div class="viewer">
  <aside class="panel">
    <a href="#/">← Lobby</a>
    <h2>Character viewer</h2>

    <h3>Class</h3>
    <div class="row">
      {#each CLASS_IDS as id (id)}
        <button
          class:active={classId === id}
          style:color={CLASSES[id].color}
          onclick={() => pickClass(id)}>{CLASSES[id].name}</button
        >
      {/each}
    </div>
    <h3>Character</h3>
    <div class="grid">
      {#each CHARACTERS as id (id)}
        <button
          class="small"
          class:active={character === id}
          onclick={() => (character = id)}
          >{id.replace('character-', '')}</button
        >
      {/each}
    </div>
    {#each SIDES as side (side)}
      <h3>{side === 'right' ? 'Right' : 'Left'} hand</h3>
      <div class="grid">
        {#each ITEMS as id (id)}
          <button
            class="small"
            class:active={hands[side] === id}
            onclick={() => (hands[side] = id)}>{id ?? 'empty'}</button
          >
        {/each}
      </div>
    {/each}

    <h3>Nameplate</h3>
    <div class="row">
      {#each TEAMS as id (id)}
        <button class:active={team === id} onclick={() => (team = id)}
          >{id}</button
        >
      {/each}
      <label><input type="checkbox" bind:checked={targeted} /> Targeted</label>
    </div>
    <label class="slider"
      >Health {Math.round(health * 100)}%
      <input type="range" min="0" max="1" step="0.01" bind:value={health} />
    </label>

    <h3>Stance</h3>
    <div class="grid">
      {#each STANCES as id (id)}
        <button
          class="small"
          class:active={stance === id}
          onclick={() => (stance = id)}>{id}</button
        >
      {/each}
    </div>
    <label
      ><input type="checkbox" bind:checked={circling} /> Run in a circle</label
    >

    <h3>Weapon trail</h3>
    <div class="row">
      <button
        class="small"
        class:active={trailClass === null}
        onclick={() => (trailClass = null)}>off</button
      >
      {#each CLASS_IDS as id (id)}
        <button
          class="small"
          class:active={trailClass === id}
          style:color={CLASSES[id].color}
          onclick={() => (trailClass = id)}>{CLASSES[id].name}</button
        >
      {/each}
    </div>
    <div class="row">
      {#each TRAIL_STYLE_IDS as id (id)}
        <button
          class="small"
          class:active={trailStyle === id}
          onclick={() => (trailStyle = id)}>{id}</button
        >
      {/each}
    </div>
    <label class="slider"
      >Length {trailSeconds.toFixed(2)}s
      <input
        type="range"
        min="0.1"
        max="1.5"
        step="0.05"
        bind:value={trailSeconds}
      />
    </label>

    <h3>Abilities</h3>
    <div class="row">
      <label><input type="checkbox" bind:checked={dummy} /> Target dummy</label>
      <button class="small" disabled={!dummy} onclick={() => engine?.swing()}
        >Auto-attack</button
      >
      <button class="small" onclick={() => engine?.clearAuras()}
        >Clear auras</button
      >
    </div>
    {#if dummy}
      <label class="slider"
        >Dummy distance {distance}
        <input type="range" min="2" max="35" step="1" bind:value={distance} />
      </label>
    {/if}
    <div class="grid">
      {#each spellbook(classId) as id (id)}
        <button
          class="small"
          title={abilityDef(id).description}
          onclick={() => engine?.useAbility(id)}>{castLabel(id)}</button
        >
      {/each}
    </div>

    <h3>Animations</h3>
    <div class="grid">
      {#each clips as clip (clip)}
        <button class="small" onclick={() => engine?.play(clip)}>{clip}</button>
      {/each}
    </div>

    <h3>
      Auras <button class="small" onclick={() => (auras = [])}>Clear</button>
    </h3>
    <div class="auras">
      {#each AURA_IDS as id (id)}
        <label
          class:harmful={auraDef(id).harmful}
          title={auraDef(id).description}
        >
          <input type="checkbox" value={id} bind:group={auras} />
          {auraDef(id).name}
        </label>
      {/each}
    </div>

    <h3>Playback</h3>
    <label class="slider"
      >Speed {timeScale.toFixed(2)}×
      <input type="range" min="0" max="2" step="0.05" bind:value={timeScale} />
    </label>
    <label><input type="checkbox" bind:checked={autoRotate} /> Turntable</label>
    <p class="muted small">
      Left drag orbit · Right drag pan · Wheel zoom. Speed 0 pauses.
    </p>
  </aside>
  <div class="view">
    <canvas bind:this={canvas}></canvas>
    <div class="overlay" bind:this={overlay}></div>
  </div>
</div>

<style>
  .viewer {
    display: grid;
    grid-template-columns: 320px 1fr;
    height: 100%;
  }
  aside {
    overflow-y: auto;
    border-radius: 0;
  }
  aside a {
    color: var(--gold);
  }
  .view {
    position: relative;
    min-width: 0;
  }
  canvas {
    display: block;
    width: 100%;
    height: 100%;
  }
  .overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin: 6px 0;
  }
  .grid,
  .auras {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
  }
  .auras label {
    font-size: 12px;
    color: #9fdc8f;
  }
  .auras label.harmful {
    color: #ff8a7a;
  }
  .slider {
    display: block;
    margin: 6px 0;
  }
  .slider input {
    width: 100%;
  }
  .small {
    font-size: 12px;
    padding: 4px 6px;
  }
  .active {
    border-color: #fff;
    background: #5a4520;
  }
  .muted {
    color: var(--muted);
  }
</style>
